import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight, MessageCircle, ArrowRight, ShieldCheck, FileText, Quote, CalendarDays } from "lucide-react";
import ReviewerPanel from "../components/ReviewerPanel";
import { AdviceNotice } from "../components/askcassie/AdviceNotice";
import ShareAnswer from "../components/ShareAnswer";
import Seo from "../components/Seo";
import { publicApi } from "../lib/api";
import { trackAsk, trackAskView } from "../lib/askTrack";
import NotFound from "./NotFound";

const askCassie = (question) =>
  window.dispatchEvent(new CustomEvent("cass:ask-cassie", { detail: { question } }));

const Paras = ({ text }) => (
  <>
    {String(text || "")
      .split("\n\n")
      .filter(Boolean)
      .map((p, i) => (
        <p key={i} className="text-slate-600 leading-[1.85] mt-4 first:mt-0">
          {p.trim()}
        </p>
      ))}
  </>
);

export default function AskCassieAnswer({ answer }) {
  const { slug } = useParams();
  const [a, setA] = useState(answer || null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (answer) {
      setA(answer);
      return;
    }
    setA(null);
    setMissing(false);
    publicApi.askCassieAnswer(slug).then(setA).catch(() => setMissing(true));
  }, [slug, answer]);

  useEffect(() => {
    if (a?.slug) trackAskView("answer", { slug: a.slug, category: a.category || "" });
  }, [a?.slug, a?.category]);

  if (missing) return <NotFound />;
  if (!a) return <div className="py-32 text-center text-sm text-slate-400">Loading…</div>;

  return (
    <div className="bg-white">
      <Seo title={a.question} description={a.quick_answer} type="article" noindex={a.noindex} />

      <article className="max-w-3xl mx-auto px-5 sm:px-8 pt-10 pb-20 md:pt-14">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-xs text-slate-400" data-testid="ask-answer-breadcrumb">
          <Link to="/" className="hover:text-navy">Home</Link>
          <ChevronRight size={12} />
          <Link to="/ask-cassie" className="hover:text-navy">Ask Cassie</Link>
          <ChevronRight size={12} />
          <Link to={`/ask-cassie/${a.category}`} className="hover:text-navy">{a.category_name}</Link>
        </nav>

        <h1 className="font-display font-extrabold text-navy text-3xl sm:text-4xl lg:text-5xl mt-6 leading-[1.1]">
          {a.question}
        </h1>

        <p className="text-navy text-lg leading-[1.75] mt-6 border-l-4 border-orange pl-5" data-testid="ask-answer-quick">
          {a.quick_answer}
        </p>

        <ShareAnswer className="mt-6" path={`/ask-cassie/${a.slug}`} title={a.question} slug={a.slug} />

        <AdviceNotice className="mt-6" />

        {a.reviewer_panel ? (
          <ReviewerPanel reviewer={a.reviewer_panel} />
        ) : a.reviewer_line ? (
          <p className="flex items-start gap-2 text-xs text-slate-500 mt-6" data-testid="ask-answer-reviewer">
            <ShieldCheck size={14} className="text-teal shrink-0 mt-0.5" />
            {a.reviewer_url ? (
              <Link to={a.reviewer_url} className="hover:text-navy underline decoration-slate-300">
                {a.reviewer_line}
              </Link>
            ) : (
              a.reviewer_line
            )}
          </p>
        ) : a.updated_line ? (
          /* A practice answer is firm policy, not a reviewed technical position:
             it carries a date and no reviewer line. */
          <p className="flex items-start gap-2 text-xs text-slate-500 mt-6" data-testid="ask-answer-updated">
            <CalendarDays size={14} className="shrink-0 mt-0.5 text-slate-400" /> {a.updated_line}
          </p>
        ) : (
          <p className="flex items-start gap-2 text-xs text-slate-500 mt-6" data-testid="ask-answer-draft">
            <FileText size={14} className="shrink-0 mt-0.5" /> Draft — awaiting review by CASS. This page isn't
            indexed and carries no reviewer line until it's approved.
          </p>
        )}

        {(a.sections || []).map((s, i) => (
          <section key={i} className="mt-12" data-testid={`ask-answer-section-${i}`}>
            <h2 className="font-display font-bold text-navy text-base md:text-lg">{s.heading}</h2>
            <div className="mt-3">
              <Paras text={s.body} />
            </div>
          </section>
        ))}

        {(a.related || []).length > 0 && (
          <section className="mt-14 rounded-2xl border border-slate-200 p-6" data-testid="ask-answer-related">
            <h2 className="font-display font-bold text-navy text-base md:text-lg">Related questions</h2>
            <ul className="mt-4 space-y-3">
              {a.related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={r.url || `/ask-cassie/${a.category}`}
                    className="group inline-flex items-start gap-2 text-navy hover:text-orange transition-colors"
                  >
                    <ArrowRight size={15} className="shrink-0 mt-1 text-orange" />
                    <span className="font-semibold text-sm">{r.question}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {a.success_story && (
          <Link
            to={a.success_story.url}
            data-testid="ask-answer-story"
            className="mt-6 flex items-start gap-3 rounded-2xl bg-slate-50 hover:bg-slate-100 p-6 transition-colors"
          >
            <Quote size={18} className="text-orange shrink-0 mt-0.5" />
            <span>
              <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400 block">A client example</span>
              <span className="font-display font-bold text-navy block mt-1">{a.success_story.title}</span>
            </span>
          </Link>
        )}

        {a.service?.url && (
          <p className="mt-10">
            <Link
              to={a.service.url}
              onClick={() => trackAsk("outbound", { page: "answer", target: a.service.url, slug: a.slug })}
              data-testid="ask-answer-service"
              className="inline-flex items-center gap-2 text-navy font-bold hover:text-orange transition-colors"
            >
              {a.service.label} <ArrowRight size={16} />
            </Link>
          </p>
        )}

        <div className="mt-14 rounded-2xl bg-navy text-white p-7">
          <p className="font-display font-bold text-lg">Still have a question?</p>
          <p className="text-slate-300 text-sm mt-2 leading-relaxed">
            Cassie can pick this up with you — she'll start on this topic.
          </p>
          <div className="flex flex-wrap gap-3 mt-5">
            <button
              type="button"
              onClick={() => { trackAsk("outbound", { page: "answer", target: "cassie", slug: a.slug }); askCassie(a.question); }}
              data-testid="ask-answer-cassie"
              className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold text-sm px-5 py-2.5 rounded-full transition-colors"
            >
              <MessageCircle size={15} /> Ask Cassie
            </button>
            {a.cta?.url && (
              <Link
                to={a.cta.url}
                onClick={() => trackAsk("outbound", { page: "answer", target: a.cta.url, slug: a.slug })}
                data-testid="ask-answer-cta"
                className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white font-bold text-sm px-5 py-2.5 rounded-full transition-colors"
              >
                {a.cta.label} <ArrowRight size={15} />
              </Link>
            )}
          </div>
        </div>
      </article>
    </div>
  );
}
